/**
 * Event-zone (geofence polygon) math.
 * Ported verbatim from the React Native app's
 * src/Screens/add-location/utils/event-zone.js — framework-agnostic, no changes.
 */

export const MAX_EVENT_ZONE_POINTS = 20;
export const DUPLICATE_POINT_THRESHOLD_METERS = 2;
export const DEFAULT_ZONE_REGION_DELTA = 0.002;

const EPSILON = 0.000000001;
const EARTH_RADIUS_METERS = 6371000;

const toRadians = (degrees) => degrees * (Math.PI / 180);

const pointToXY = (coordinate) => ({
  x: Number(coordinate.longitude),
  y: Number(coordinate.latitude),
});

const orientation = (left, middle, right) => {
  const value =
    (middle.y - left.y) * (right.x - middle.x) -
    (middle.x - left.x) * (right.y - middle.y);

  if (Math.abs(value) < EPSILON) {
    return 0;
  }

  return value > 0 ? 1 : 2;
};

const isOnSegment = (left, middle, right) =>
  middle.x <= Math.max(left.x, right.x) + EPSILON &&
  middle.x + EPSILON >= Math.min(left.x, right.x) &&
  middle.y <= Math.max(left.y, right.y) + EPSILON &&
  middle.y + EPSILON >= Math.min(left.y, right.y);

const segmentsIntersect = (firstStart, firstEnd, secondStart, secondEnd) => {
  const p1 = pointToXY(firstStart);
  const q1 = pointToXY(firstEnd);
  const p2 = pointToXY(secondStart);
  const q2 = pointToXY(secondEnd);
  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);

  if (o1 !== o2 && o3 !== o4) {
    return true;
  }

  return (
    (o1 === 0 && isOnSegment(p1, p2, q1)) ||
    (o2 === 0 && isOnSegment(p1, q2, q1)) ||
    (o3 === 0 && isOnSegment(p2, p1, q2)) ||
    (o4 === 0 && isOnSegment(p2, q1, q2))
  );
};

const areAdjacentEdges = (leftIndex, rightIndex, count) =>
  leftIndex === rightIndex ||
  Math.abs(leftIndex - rightIndex) === 1 ||
  (leftIndex === 0 && rightIndex === count - 1);

export const getDistanceBetweenCoordinates = (left, right) => {
  if (!left || !right) {
    return Number.POSITIVE_INFINITY;
  }

  const lat1 = toRadians(Number(left.latitude));
  const lat2 = toRadians(Number(right.latitude));
  const deltaLat = toRadians(Number(right.latitude) - Number(left.latitude));
  const deltaLng = toRadians(Number(right.longitude) - Number(left.longitude));
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
};

export const isDuplicatePoint = (coordinate, points) =>
  points.some(
    (point) =>
      getDistanceBetweenCoordinates(coordinate, point) <
      DUPLICATE_POINT_THRESHOLD_METERS
  );

export const doesPolygonSelfIntersect = (points) => {
  if (points.length < 4) {
    return false;
  }

  for (let leftIndex = 0; leftIndex < points.length; leftIndex += 1) {
    const leftStart = points[leftIndex];
    const leftEnd = points[(leftIndex + 1) % points.length];

    for (
      let rightIndex = leftIndex + 1;
      rightIndex < points.length;
      rightIndex += 1
    ) {
      if (areAdjacentEdges(leftIndex, rightIndex, points.length)) {
        continue;
      }

      if (
        segmentsIntersect(
          leftStart,
          leftEnd,
          points[rightIndex],
          points[(rightIndex + 1) % points.length]
        )
      ) {
        return true;
      }
    }
  }

  return false;
};

export const isPointInsidePolygon = (point, polygon) => {
  if (!point || polygon.length < 3) {
    return false;
  }

  const target = pointToXY(point);
  let inside = false;

  for (
    let index = 0, previous = polygon.length - 1;
    index < polygon.length;
    previous = index++
  ) {
    const currentPoint = pointToXY(polygon[index]);
    const previousPoint = pointToXY(polygon[previous]);
    const intersects =
      (currentPoint.y > target.y) !== (previousPoint.y > target.y) &&
      target.x <
        ((previousPoint.x - currentPoint.x) * (target.y - currentPoint.y)) /
          (previousPoint.y - currentPoint.y) +
          currentPoint.x;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

export const normalizeEventZone = (zone) => {
  if (!zone) {
    return null;
  }

  let parsedZone = zone;
  try {
    parsedZone = typeof zone === "string" ? JSON.parse(zone) : zone;
  } catch (error) {
    return null;
  }

  const coordinates = (
    Array.isArray(parsedZone)
      ? parsedZone
      : parsedZone?.coordinates ??
        parsedZone?.Coordinates ??
        parsedZone?.GeofencePolygon ??
        []
  )
    .map((point, index) => ({
      latitude: Number(
        point?.latitude ?? point?.Latitude ?? point?.lat ?? point?.Lat
      ),
      longitude: Number(
        point?.longitude ?? point?.Longitude ?? point?.lng ?? point?.Lng
      ),
      order: Number(point?.order ?? point?.Order ?? index + 1),
    }))
    .filter(
      (point) =>
        Number.isFinite(point.latitude) && Number.isFinite(point.longitude)
    );

  if (coordinates.length < 3) {
    return null;
  }

  return {
    type: parsedZone?.type ?? parsedZone?.Type ?? "polygon",
    center: {
      latitude: Number(
        parsedZone?.center?.latitude ?? parsedZone?.Center?.Latitude
      ),
      longitude: Number(
        parsedZone?.center?.longitude ?? parsedZone?.Center?.Longitude
      ),
    },
    address: parsedZone?.address ?? parsedZone?.Address ?? "",
    coordinates,
  };
};

export const canAddEventZonePoint = (points) =>
  points.length < MAX_EVENT_ZONE_POINTS;

/** Build the `geofencePolygonJson` string expected by GenerateELQR/UpdateELQR. */
export const buildGeofencePolygonJson = (coordinates) =>
  coordinates?.length
    ? JSON.stringify(
        coordinates.map((point) => ({
          lat: Number(point.latitude),
          lng: Number(point.longitude),
        }))
      )
    : null;
